import { defineStore } from 'pinia'
import { computed, onScopeDispose, ref } from 'vue'
import type { User } from '@/types'
import { hasPermission, homeForRole, normalizeRole, roleLabel, type Permission } from '@/auth/access'
import { clearSession, currentSession, saveSession, subscribeSession, updateSessionUser } from '@/auth/session'

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(currentSession().user)
  const token = ref<string | null>(currentSession().token)
  const unsubscribe = subscribeSession((session) => {
    token.value = session.token
    user.value = session.user
  })
  onScopeDispose(unsubscribe)

  const userRole = computed(() => normalizeRole(user.value?.role))
  const isLoggedIn = computed(() => !!token.value && !!userRole.value)
  const userName = computed(() => user.value?.name || '')
  const userRoleLabel = computed(() => roleLabel(userRole.value))
  const home = computed(() => homeForRole(userRole.value))
  const can = (permission: Permission) => isLoggedIn.value && hasPermission(userRole.value, permission)
  let refresh: Promise<void> | undefined

  function setAuth(newToken: string, newUser: User) { saveSession(newToken, newUser) }
  function logout() { clearSession() }
  function refreshSession(): Promise<void> {
    if (!token.value) return Promise.resolve()
    if (refresh) return refresh
    const expectedToken = token.value
    // This background refresh must not pull the HTTP client into the boot bundle.
    refresh = import('@/api/auth')
      .then(({ getCurrentUser }) => {
        if (currentSession().token !== expectedToken) return
        return getCurrentUser().then((freshUser) => updateSessionUser(freshUser, expectedToken))
      })
      .finally(() => { refresh = undefined })
    return refresh
  }

  return { user, token, isLoggedIn, userName, userRole, userRoleLabel, home, can, setAuth, logout, refreshSession }
})
